export type Route =
  | { name: 'interaction'; uid: string }
  | { name: 'forgot-password' }
  | { name: 'reset-password'; token: string }
  | { name: 'error' }
  | { name: 'not-found' };

const INTERACTION = /^\/interaction\/([A-Za-z0-9_-]+)\/?$/;

/**
 * Hand-rolled so the SPA carries no router dependency — CloudFront serves
 * index.html for every path (ADR 0009).
 *
 * The two recovery routes are the exception to "every screen belongs to an
 * interaction": a locked-out user has no session and no pending authorization
 * request (docs/auth/40-features/password-reset.md).
 */
export function matchRoute(pathname: string, hash = ''): Route {
  const interaction = INTERACTION.exec(pathname);
  if (interaction?.[1]) {
    return { name: 'interaction', uid: interaction[1] };
  }

  const path = pathname.replace(/\/+$/, '') || '/';
  // Terminal errors outside an interaction. The social callback redirects here
  // when its state is unusable — a constant, parameter-free path, so nothing
  // attacker-influenced rides along (docs/auth/20-architecture/component-web-auth.md).
  if (path === '/error') {
    return { name: 'error' };
  }
  if (path === '/forgot-password') {
    return { name: 'forgot-password' };
  }
  if (path === '/reset-password') {
    // The token rides in the FRAGMENT (`#token=…`), never the query, so it is
    // never sent to a server or logged (password-reset.md). An absent token is
    // not an error state of its own: submitting it fails exactly like a stale
    // one, so there is nothing to disclose here.
    const token =
      new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? '';
    return { name: 'reset-password', token };
  }

  return { name: 'not-found' };
}
